import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  addPrediction,
  deleteAll,
  deletePrediction,
  fetchPredictionList,
  updatePrediction,
} from "../../services/prediction.service";
import { enqueueSnackbar } from "notistack";
import axios, { AxiosProgressEvent } from "axios";

// fetch prediction
export const doFetchPredictionList = createAsyncThunk(
  "prediction/fetchPredictionList",
  async (payload: { search?: string; page: number; limit: number }) => {
    try {
      const response: any = await fetchPredictionList({
        search: payload.search,
        page: payload.page,
        limit: payload.limit,
      });
      return response;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch prediction list";
      enqueueSnackbar(errorMessage, { variant: "error" });
      throw errorMessage;
    }
  }
);

// add prediction
export const doAddPrediction = createAsyncThunk(
  "prediction/addPrediction",
  async (body: any, { getState, rejectWithValue }) => {
    const accessToken = localStorage.getItem("token");
    if (!accessToken) {
      throw new Error("Authentication token not found");
    }
    try {
      const response: any = await addPrediction(accessToken, body);
      return response;
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || "Failed to add prediction",
        { variant: "error" }
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to add prediction"
      );
    }
  }
);

// update prediction
export const doUpdatePrediction = createAsyncThunk(
  "prediction/updatePrediction",
  async (payload: { id: string; body: any }, { rejectWithValue }) => {
    try {
      const response: any = await updatePrediction(payload.id, payload.body);
      enqueueSnackbar(response.message, { variant: "success" });
      return response;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Failed to update prediction";
      enqueueSnackbar(errorMessage, { variant: "error" });
      return rejectWithValue(errorMessage);
    }
  }
);

// delete prediction
export const doDelPrediction = createAsyncThunk(
  "prediction/delPrediction",
  async (id: string) => {
    try {
      const response: any = await deletePrediction(id);
      enqueueSnackbar(response.message, { variant: "success" });
      return response;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Failed to delete prediction";
      enqueueSnackbar(errorMessage, { variant: "error" });
      throw errorMessage;
    }
  }
);

// clear all data
export const doClearAll = createAsyncThunk("prediction/clearData", async () => {
  try {
    const response: any = await deleteAll();
    enqueueSnackbar(response.message, { variant: "success" });
    return response;
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message || "Failed to clear the data";
    enqueueSnackbar(errorMessage, { variant: "error" });
    throw errorMessage;
  }
});

// export prediction
export const exportPredictions = async (): Promise<void> => {
  try {
    const getAuthToken = () => {
      return localStorage.getItem("token") || "{}";
    };
    const token = getAuthToken();
    const response = await axios.get(
      `${process.env.REACT_APP_ADMIN_API_URL}/predictions/export`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "*/*",
        },
        responseType: "blob",
      }
    );

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "predictions.xlsx");
    document.body.appendChild(link);
    link.click();

    // Cleanup
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
  } catch (error: any) {
    console.error("Error exporting predictions:", error);
    alert("Failed to export predictions");
  }
};

export const doHandleFileUpload = createAsyncThunk(
  "prediction/uploadFile",
  async (
    {
      file,
      onUploadProgress,
    }: {
      file: File;
      onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
    },
    { rejectWithValue }
  ) => {
    if (!file) return rejectWithValue("No file selected");

    const formData = new FormData();
    formData.append("file", file);

    try {
      const getAuthToken = () => {
        return localStorage.getItem("token") || "{}";
      };
      const token = getAuthToken();
      const response = await axios.post(
        `${process.env.REACT_APP_ADMIN_API_URL}/predictions/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          onUploadProgress,
        }
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to upload file"
      );
    }
  }
);
